import { useLayoutEffect, useMemo, useState } from "react";

import { createColumnHelper } from "@tanstack/react-table";
import { Building2, MoreVertical, Plus, SearchX } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { SectionLayout } from "@/components/layout/SectionLayout";
import { ListPageLayout } from "@/components/layout/ListPageLayout";
import { LoadErrorMessage } from "@/components/LoadErrorMessage";
import { ListTable, ListTableSkeleton } from "@/components/ListTable";
import {
  Button,
  EmptyState,
  IconButton,
  InlineMessage,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  SearchFilterBar,
} from "@nexus/ui";
import { useTenants } from "@/hooks/useTenants";
import { useAppContext } from "@/contexts/AppContext";
import { formatAdminDateTime, type TimezoneMode } from "@/lib/dateFormat";
import type { Tenant } from "@/types";

import { ADMIN_NAV } from "./adminNav";
import { useIsAdmin } from "@/hooks/useAccount";
import { useAccessCatalog, useAdminAccounts, type ProfileAccount } from "./useAdminAccounts";
import { countLabel, tenantAccess, type Organization } from "./userAccessModel";
import { DeleteTenantDialog } from "./components/TenantDialogs";
import { tenantDeletionNotice, useTenantAdmin } from "./useTenantAdmin";

const columnHelper = createColumnHelper<Tenant>();
const tenantHref = (tenant: Tenant) => `/admin/tenants/${encodeURIComponent(tenant.tenant_id)}`;

function TenantTitle({ tenant, showId }: { tenant: Tenant; showId: boolean }) {
  return (
    <div className="min-w-0">
      <Link
        to={tenantHref(tenant)}
        className="font-medium text-primary group-hover/row:underline focus-visible:underline"
      >
        {tenant.tenant_label}
      </Link>
      {showId ? <p className="font-mono text-xs text-muted">ID {tenant.tenant_id}</p> : null}
    </div>
  );
}

const muted = (value: string | null) => (
  <span className="text-sm text-secondary">{value || "—"}</span>
);

/** Client accounts reading a tenant: directly, through its customer or through global access. */
function usersOf(
  tenant: Tenant,
  accounts: readonly ProfileAccount[],
  organizations: readonly Organization[],
) {
  return accounts.filter((account) => {
    const access = tenantAccess(account, tenant.tenant_id, organizations);
    return access !== null && access.via !== "profile";
  });
}

interface TenantActions {
  accounts: readonly ProfileAccount[];
  organizations: readonly Organization[];
  accountsLoaded: boolean;
  onAddUsers: (tenant: Tenant) => void;
  onDelete: (tenant: Tenant) => void;
}

function TenantRowMenu({
  tenant,
  onAddUsers,
  onDelete,
}: { tenant: Tenant } & Pick<TenantActions, "onAddUsers" | "onDelete">) {
  const navigate = useNavigate();
  return (
    <Menu>
      <MenuTrigger asChild>
        <IconButton label={`Actions for ${tenant.tenant_label}`} className="size-8">
          <MoreVertical aria-hidden className="size-4" />
        </IconButton>
      </MenuTrigger>
      <MenuContent align="end" className="space-y-1 p-1.5">
        <MenuItem className="min-h-9" onSelect={() => navigate(tenantHref(tenant))}>
          View tenant
        </MenuItem>
        <MenuItem className="min-h-9" onSelect={() => navigate(`${tenantHref(tenant)}?edit=1`)}>
          Edit tenant
        </MenuItem>
        <MenuItem className="min-h-9" onSelect={() => onAddUsers(tenant)}>
          Add users
        </MenuItem>
        <MenuSeparator />
        <MenuItem className="min-h-9" onSelect={() => onDelete(tenant)} tone="danger">
          Delete tenant
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

function UserCount({ tenant, actions }: { tenant: Tenant; actions: TenantActions }) {
  if (!actions.accountsLoaded) return <span className="text-sm text-muted">—</span>;
  const users = usersOf(tenant, actions.accounts, actions.organizations);
  return (
    <Link
      to={`${tenantHref(tenant)}#tenant-users-heading`}
      className="text-sm tabular-nums text-primary hover:underline"
      aria-label={`${countLabel(users.length, "user")} for ${tenant.tenant_label}`}
    >
      {users.length}
    </Link>
  );
}

function tenantColumns(timezone: TimezoneMode, actions?: TenantActions) {
  return [
    columnHelper.accessor("tenant_label", {
      header: "Tenant",
      cell: ({ row }) => <TenantTitle tenant={row.original} showId={Boolean(actions)} />,
    }),
    columnHelper.accessor("customer", {
      header: "Customer",
      cell: ({ getValue }) => muted(getValue()),
    }),
    columnHelper.accessor("tenant_region", {
      header: "Region",
      cell: ({ getValue }) => muted(getValue()),
    }),
    columnHelper.accessor("creation_date", {
      header: "Created",
      cell: ({ getValue }) => (
        <span className="whitespace-nowrap text-sm text-secondary">
          {formatAdminDateTime(getValue(), timezone) || <span className="text-muted">&mdash;</span>}
        </span>
      ),
    }),
    ...(actions
      ? [
          columnHelper.display({
            id: "users",
            header: "Users",
            cell: ({ row }) => <UserCount tenant={row.original} actions={actions} />,
          }),
          columnHelper.display({
            id: "actions",
            header: () => <span className="sr-only">Actions</span>,
            cell: ({ row }) => (
              <TenantRowMenu
                tenant={row.original}
                onAddUsers={actions.onAddUsers}
                onDelete={actions.onDelete}
              />
            ),
          }),
        ]
      : []),
  ];
}

function TenantTable({ tenants, actions }: { tenants: Tenant[]; actions?: TenantActions }) {
  const { timezone } = useAppContext();
  const navigate = useNavigate();
  const sortedTenants = useMemo(
    () =>
      [...tenants].sort((left, right) =>
        left.tenant_label.localeCompare(right.tenant_label, undefined, {
          sensitivity: "base",
        }),
      ),
    [tenants],
  );
  const columns = useMemo(() => tenantColumns(timezone, actions), [timezone, actions]);

  return (
    <ListTable
      data={sortedTenants}
      columns={columns}
      getRowId={(tenant) => tenant.tenant_id}
      rowHasLink
      onRowClick={(tenant) => navigate(tenantHref(tenant))}
      renderMobileCard={(tenant) => (
        <>
          <div className="flex items-start justify-between gap-2">
            <TenantTitle tenant={tenant} showId={Boolean(actions)} />
            {actions ? (
              <TenantRowMenu
                tenant={tenant}
                onAddUsers={actions.onAddUsers}
                onDelete={actions.onDelete}
              />
            ) : null}
          </div>
          <p className="mt-1 flex flex-wrap gap-x-2 text-xs text-secondary">
            <span>{tenant.customer || "No customer"}</span>
            <span aria-hidden className="text-muted">
              ·
            </span>
            <span>{tenant.tenant_region || "No region"}</span>
            {actions ? (
              <>
                <span aria-hidden className="text-muted">
                  ·
                </span>
                <span>
                  {actions.accountsLoaded
                    ? countLabel(
                        usersOf(tenant, actions.accounts, actions.organizations).length,
                        "user",
                      )
                    : "Users unavailable"}
                </span>
              </>
            ) : null}
          </p>
          <p className="mt-2 text-xs text-muted">
            Created {formatAdminDateTime(tenant.creation_date, timezone) || <>&mdash;</>}
          </p>
        </>
      )}
    />
  );
}

function TenantTableSkeleton({ columnCount }: { columnCount: number }) {
  return <ListTableSkeleton label="Loading tenants" columnCount={columnCount} />;
}

export default function TenantListPage() {
  const isAdmin = useIsAdmin();
  const { data: tenants = [], isLoading, isError, refetch } = useTenants();
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("");
  const regionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const tenant of tenants) {
      if (tenant.tenant_region)
        counts.set(tenant.tenant_region, (counts.get(tenant.tenant_region) ?? 0) + 1);
    }
    return [...counts].sort(([a], [b]) => a.localeCompare(b));
  }, [tenants]);
  const query = search.trim().toLowerCase();
  const filtered = tenants.filter(
    (tenant) =>
      (!region || tenant.tenant_region === region) &&
      [tenant.tenant_label, tenant.customer, tenant.tenant_region]
        .join(" ")
        .toLowerCase()
        .includes(query),
  );
  const isFiltered = Boolean(query || region);
  const location = useLocation();
  const navigate = useNavigate();
  const accounts = useAdminAccounts(isAdmin);
  const catalog = useAccessCatalog(isAdmin);
  const admin = useTenantAdmin();
  const [deleteTenant, setDeleteTenant] = useState<Tenant | null>(null);
  const [notice, setNotice] = useState("");
  const actions = useMemo<TenantActions | undefined>(
    () =>
      isAdmin
        ? {
            accounts: accounts.data ?? [],
            organizations: catalog.data?.organizations ?? [],
            accountsLoaded: accounts.isSuccess && catalog.isSuccess,
            onAddUsers: (tenant) => {
              navigate(`/admin/tenants/${encodeURIComponent(tenant.tenant_id)}?attach=1`);
            },
            onDelete: (tenant) => {
              admin.remove.reset();
              setDeleteTenant(tenant);
            },
          }
        : undefined,
    // The mutation objects change on every render; their reset is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin, accounts.data, accounts.isSuccess, catalog.data, catalog.isSuccess],
  );

  function openCreate() {
    navigate("/admin/tenants/new");
  }

  useLayoutEffect(() => {
    if (location.pathname === "/admin") {
      navigate("/admin/tenants", { replace: true });
    }
  }, [location.pathname, navigate]);

  const deletionNotice = (location.state as { tenantDeletionNotice?: string } | null)
    ?.tenantDeletionNotice;

  return (
    <SectionLayout
      sectionTitle="Administration"
      sidebarItems={isAdmin ? ADMIN_NAV : ADMIN_NAV.filter((item) => item.id === "admin-tenants")}
      title="Tenants"
      breadcrumbTrail={[{ label: "Administration", href: "/admin" }, { label: "Tenants" }]}
      description={
        isAdmin
          ? "Tenants, their customer and region, and the client users who read them."
          : "Browse the tenants available to this account and their customer details."
      }
      actions={
        isAdmin ? (
          <Button variant="primary" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            Create tenant
          </Button>
        ) : undefined
      }
    >
      <ListPageLayout
        toolbar={
          !isLoading && !isError && tenants.length > 0 ? (
            <SearchFilterBar
              label="Search tenants"
              placeholder="Search tenants or customers…"
              query={search}
              onQueryChange={setSearch}
              className="min-h-10 py-1"
              facets={[
                {
                  id: "region",
                  label: "Region",
                  multiple: false,
                  options: regionCounts.map(([value, count]) => ({ value, label: value, count })),
                  selected: region ? [region] : [],
                  onChange: (next) => setRegion(next[0] ?? ""),
                },
              ]}
              resultSummary={
                isFiltered
                  ? `${filtered.length} of ${tenants.length}`
                  : `${tenants.length} ${tenants.length === 1 ? "tenant" : "tenants"}`
              }
            />
          ) : undefined
        }
      >
        {notice || deletionNotice ? (
          <InlineMessage tone="success" title={notice || deletionNotice} className="mb-4">
            {null}
          </InlineMessage>
        ) : null}
        {isError && (
          <LoadErrorMessage title="Tenants could not be loaded" onRetry={() => void refetch()} />
        )}
        {isAdmin && (accounts.isError || catalog.isError) && (
          <LoadErrorMessage
            title="User counts could not be loaded"
            onRetry={() => {
              void accounts.refetch();
              void catalog.refetch();
            }}
          />
        )}

        {isLoading && !isError && <TenantTableSkeleton columnCount={isAdmin ? 6 : 4} />}

        {!isLoading && !isError && tenants.length === 0 && (
          <EmptyState
            icon={Building2}
            title="No tenants available"
            description={
              isAdmin
                ? "Create a tenant to get started."
                : "Tenants will appear here when they are provisioned for this account."
            }
            action={
              isAdmin ? (
                <Button variant="secondary" size="sm" onClick={openCreate}>
                  Create tenant
                </Button>
              ) : undefined
            }
          />
        )}

        {!isLoading && !isError && filtered.length > 0 && (
          <TenantTable tenants={filtered} actions={actions} />
        )}
        {!isLoading && !isError && tenants.length > 0 && filtered.length === 0 && (
          <EmptyState
            icon={SearchX}
            title="No matching tenants"
            description="Try another name, customer or region."
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSearch("");
                  setRegion("");
                }}
              >
                Clear search and filters
              </Button>
            }
          />
        )}
      </ListPageLayout>
      {isAdmin ? (
        <>
          <DeleteTenantDialog
            tenant={deleteTenant}
            isPending={admin.remove.isPending}
            error={admin.remove.error}
            onClose={() => setDeleteTenant(null)}
            onConfirm={() => {
              if (!deleteTenant) return;
              admin.remove.mutate(deleteTenant.tenant_id, {
                onSuccess: (result) => {
                  setDeleteTenant(null);
                  setNotice(tenantDeletionNotice(result.soft_deleted));
                },
              });
            }}
          />
        </>
      ) : null}
    </SectionLayout>
  );
}
